import ApiError from '../../../components/Errors/ApiError';
import SectionHeading from '../../../components/SectionHeading';
import styled, { css, mediaQuery, useMediaQuery } from 'academy-common-lib/utils/Styling';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIButton from 'UIComponents/button/UIButton';
import I18n from 'I18n';
import { ConditionalNavMarker } from '../../rhumb';
import { LearningContentItemCard } from '../../cards';
import ContentCardSkeleton from '../../../components/TrackCard/ContentCardSkeleton';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { debounce } from 'underscore';
import ReducedCardSkeleton from '../../../components/ContentCards/ReducedContentCard/subcomponents/ReducedCardSkeleton';
import UsageTracker from '../../../utils/UsageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_CARDS_FOR_MOBILE = 3;
const DEFAULT_MIN_CARD_SIZE = {
  large: 250,
  medium: 380,
  small: 340
};
const ChevronContainer = styled(UIFlex).attrs({
  direction: 'column',
  align: 'center',
  justify: 'center'
}).withConfig({
  displayName: "CardsSection__ChevronContainer",
  componentId: "sc-18w8khl-0"
})(["width:0 !important;max-height:204px;> button{transform:translateX(", "24px);}"], ({
  $isPrevious
}) => $isPrevious ? '-' : '');
const ResponsiveRow = styled.div.withConfig({
  displayName: "CardsSection__ResponsiveRow",
  componentId: "sc-18w8khl-1"
})(["display:grid;width:100%;grid-template-columns:repeat( auto-fill,minmax( ", "px,1fr ) );grid-template-rows:1fr;grid-gap:24px;grid-auto-rows:0;row-gap:0;", ""], ({
  minCardSize,
  $size
}) => minCardSize || DEFAULT_MIN_CARD_SIZE[$size], ({
  displayFullCardsOnMobile
}) => displayFullCardsOnMobile && mediaQuery('mobile', css(["display:flex;flex-direction:column;gap:24px;"])));
export const useSetMaxCards = ({
  displayFullCardsOnMobile,
  renderArea,
  setMaxCards
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const debouncedUpdate = useMemo(() => debounce(() => {
    if (!setMaxCards || !renderArea.current) return;
    if (isMobile && displayFullCardsOnMobile) {
      setMaxCards(MAX_CARDS_FOR_MOBILE);
    } else {
      const cardsThatFitOnAreaCount = window.getComputedStyle(renderArea.current).getPropertyValue('grid-template-columns').split(' ').length;
      setMaxCards(cardsThatFitOnAreaCount);
    }
  }, 200), [displayFullCardsOnMobile, isMobile, renderArea, setMaxCards]);
  useLayoutEffect(() => {
    if (!setMaxCards || !renderArea.current) return;
    debouncedUpdate();
    window.addEventListener('resize', debouncedUpdate);
    return () => window.removeEventListener('resize', debouncedUpdate);
  }, [debouncedUpdate, setMaxCards, renderArea]);
};
export const CardsSection = ({
  title,
  description,
  error,
  isLoading: isLoadingProp,
  content,
  appearsIn,
  navMarker,
  errorMarker,
  showNext: showNextParam,
  showPrevious: showPreviousParam,
  onClickNext,
  onClickPrevious,
  onItemClick,
  use,
  showDescription,
  setMaxCards,
  displayFullCardsOnMobile,
  minCardSize,
  showOverview,
  size = 'large'
}) => {
  const {
    isTablet
  } = useMediaQuery();
  const cardsSectionRef = useRef(null);
  useSetMaxCards({
    displayFullCardsOnMobile,
    renderArea: cardsSectionRef,
    setMaxCards
  });
  const handleOnNextClick = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'card-section-next-click',
      appearsIn
    });
    if (onClickNext) onClickNext();
  }, [appearsIn, onClickNext]);
  const handleOnPreviousClick = useCallback(() => {
    UsageTracker.track('interaction', {
      action: 'card-section-previous-click',
      appearsIn
    });
    if (onClickPrevious) onClickPrevious();
  }, [appearsIn, onClickPrevious]);
  const handleOnItemClick = useCallback(item => {
    UsageTracker.track('interaction', {
      action: 'card-section-item-click',
      id: item.id,
      language: item.language,
      type: item.type,
      appearsIn
    });
    if (onItemClick) onItemClick(item);
  }, [appearsIn, onItemClick]);
  const rows = useMemo(() => content && content.map((item, i) => /*#__PURE__*/_jsx(LearningContentItemCard, {
    appearsIn: appearsIn,
    item: item,
    onItemClick: handleOnItemClick,
    showDescription: showDescription,
    showOverview: showOverview,
    size: size,
    use: use
  }, `${item.id}-${item.type}-${item.language}-{${i}}`)), [appearsIn, content, handleOnItemClick, showDescription, showOverview, size, use]);
  if (error) return /*#__PURE__*/_jsx(ConditionalNavMarker, {
    name: errorMarker,
    children: /*#__PURE__*/_jsx(ApiError, {
      error: error
    })
  });
  const isLoading = isLoadingProp || !rows;
  if (!isLoading && !rows.length) return null; // if empty results don't render the component

  const showNext = !isLoading && showNextParam && (size !== 'large' || !isTablet);
  const showPrevious = !isLoading && showPreviousParam && (size !== 'large' || !isTablet);
  return /*#__PURE__*/_jsxs("div", {
    "data-loading": isLoading,
    style: {
      width: '100%'
    },
    children: [title && /*#__PURE__*/_jsx(SectionHeading, {
      subText: description,
      children: title
    }), /*#__PURE__*/_jsx(ConditionalNavMarker, {
      name: navMarker,
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "stretch",
        children: [showPrevious && /*#__PURE__*/_jsx(ChevronContainer, {
          $isPrevious: 1,
          children: /*#__PURE__*/_jsx(UIButton, {
            "aria-label": I18n.text('learningCenter.common.loadPreviousButton'),
            onClick: handleOnPreviousClick,
            use: "unstyled",
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "left",
              size: "medium"
            })
          })
        }), /*#__PURE__*/_jsx(ResponsiveRow, {
          $size: size,
          displayFullCardsOnMobile: displayFullCardsOnMobile ? 1 : 0,
          minCardSize: minCardSize,
          ref: cardsSectionRef,
          style: {
            overflow: isLoading ? 'hidden' : 'none'
          },
          children: isLoading ? [...Array(5)].map((_, i) => size === 'large' ? /*#__PURE__*/_jsx(ContentCardSkeleton, {
            use: use
          }, i) : /*#__PURE__*/_jsx(ReducedCardSkeleton, {
            size: size,
            use: use
          })) : rows
        }), showNext && /*#__PURE__*/_jsx(ChevronContainer, {
          $isPrevious: 0,
          children: /*#__PURE__*/_jsx(UIButton, {
            "aria-label": I18n.text('learningCenter.common.loadNextButton'),
            onClick: handleOnNextClick,
            use: "unstyled",
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "right",
              size: "medium"
            })
          })
        })]
      })
    })]
  });
};
export default CardsSection;